import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { AdminApiError, adminErrorMessage } from './api';
import { ACCOUNTING_MAX_OFFSET, canRetryAccounting, fetchAccounting, retryAccounting } from './accountingApi';
import type { AccountingPage, AccountingRequest } from './accountingApi';
import { ComponentRegistry } from './ComponentRegistry';
import { accountingStatusLabels, valueLabel } from './labels';
import { formatUnits } from '../../core/money';
import styles from './admin.module.css';

function accessFailure(error: unknown): error is AdminApiError {
  return error instanceof AdminApiError && (error.status === 401 || error.status === 403);
}

function retryReason(row: AccountingRequest): string {
  if (row.recordedUsage === null) return '用量未知，需要人工核对。';
  if (row.status === 'reconciliation_required' || row.status === 'preparing' || row.status === 'running') return '需要人工核对。';
  return '暂不可重试，请刷新确认。';
}

export function ChatAccounting({ onAccessFailure, onRetryingChange }: {
  onAccessFailure: (error: AdminApiError) => void;
  onRetryingChange: (retrying: boolean) => void;
}): ReactElement {
  const [offset, setOffset] = useState(0);
  const [revision, setRevision] = useState(0);
  const [page, setPage] = useState<AccountingPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const retryAbort = useRef<AbortController | null>(null);
  const busy = loading || retrying !== null;

  useEffect(() => () => {
    retryAbort.current?.abort();
    retryAbort.current = null;
    onRetryingChange(false);
  }, [onRetryingChange]);

  useEffect(() => {
    const abort = new AbortController();
    setLoading(true); setPage(null);
    void fetchAccounting(offset, abort.signal).then((next) => {
      if (!abort.signal.aborted) setPage(next);
    }).catch((failure: unknown) => {
      if (abort.signal.aborted) return;
      if (accessFailure(failure)) onAccessFailure(failure);
      else { setError((current) => current || adminErrorMessage(failure)); setNotice(''); }
    }).finally(() => { if (!abort.signal.aborted) setLoading(false); });
    return () => abort.abort();
  }, [offset, revision, onAccessFailure]);

  function refresh(nextOffset = offset) {
    if (busy) return;
    setError(''); setNotice(''); setOffset(nextOffset); setRevision((value) => value + 1);
  }

  async function retry(row: AccountingRequest) {
    if (busy || retryAbort.current || !canRetryAccounting(row)) return;
    const abort = new AbortController(); retryAbort.current = abort;
    setRetrying(row.requestId); onRetryingChange(true); setError(''); setNotice('');
    try {
      await retryAccounting(row, abort.signal);
      if (!abort.signal.aborted) setNotice('结算已完成。');
    } catch (failure) {
      if (abort.signal.aborted) return;
      if (accessFailure(failure)) { onAccessFailure(failure); return; }
      setError(adminErrorMessage(failure));
    } finally {
      if (!abort.signal.aborted) {
        retryAbort.current = null; setRetrying(null); onRetryingChange(false); setLoading(true); setPage(null);
        // A timeout or failed response may still have changed accounting. Always reread.
        setRevision((value) => value + 1);
      }
    }
  }

  return <section aria-label="对话计费恢复" aria-busy={busy}>
    <ComponentRegistry id="admin.accounting.help">
      <p>重试只会结算已记录的用量，不会再次发起模型请求。用量未知的请求需要人工核对。</p>
      {page && <p className={styles.accountingHint}>准备中或进行中的请求超过 {page.staleAfterSeconds / 60} 分钟未更新即标记为停滞。</p>}
      <div className={styles.actions}><button type="button" disabled={busy} onClick={() => refresh()}>刷新计费</button></div>
    </ComponentRegistry>
    <ComponentRegistry id="admin.accounting.status">
      {error && <p role="alert" className={styles.error}>{error}</p>}
      {notice && !error && <p role="status" className={styles.success}>{notice}</p>}
      {retrying && <p role="status">正在重试结算…</p>}
      {loading && <p role="status" className={styles.empty}>正在加载计费…</p>}
    </ComponentRegistry>
    {page && <>
      <ComponentRegistry id="admin.accounting.records" className={styles.tableScroll} tabIndex={0} aria-label="对话计费表格">
        <table><caption className={styles.srOnly}>对话计费请求</caption>
          <thead><tr>{['请求 / 归属', '模型', '状态', '已记录 token', '已扣费（元）', '预计扣费（元）', '时间', '恢复操作'].map((label) => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>{page.requests.length === 0 ? <tr><td colSpan={8} className={styles.empty}>本页暂无计费请求。</td></tr> : page.requests.map((row) => <tr key={row.requestId}>
            <td>{row.requestId}<small>归属：{row.ownerUserId}</small></td><td>{row.modelDisplayName ?? row.modelSlug ?? row.modelId ?? '已删除模型'}</td>
            <td>{valueLabel(accountingStatusLabels, row.status)}<small>{row.stale ? '已停滞' : '正常'}</small></td>
            <td>{row.recordedUsage ? <>输入：{row.recordedUsage.inputTokens}<small>输出：{row.recordedUsage.outputTokens}</small><small>缓存输入：{row.recordedUsage.cachedInputTokens}</small></> : '未知'}</td>
            <td>{row.chargedUnits === null ? '未扣费' : formatUnits(row.chargedUnits)}</td><td>{row.proposedChargeUnits === null ? '未知' : formatUnits(row.proposedChargeUnits)}</td>
            <td>创建：{new Date(row.createdAt).toLocaleString()}<small>更新：{new Date(row.updatedAt).toLocaleString()}</small></td>
            <td><button type="button" aria-label={`重试结算 ${row.requestId}`} disabled={busy || !canRetryAccounting(row)} onClick={() => void retry(row)}>{retrying === row.requestId ? '重试中…' : '重试结算'}</button>
              {!canRetryAccounting(row) && <small>{retryReason(row)}</small>}
            </td>
          </tr>)}</tbody>
        </table>
      </ComponentRegistry>
      <ComponentRegistry id="admin.accounting.pagination" className={styles.pagination}>
        <span>{page.requests.length ? `第 ${page.offset + 1}–${page.offset + page.requests.length} 条请求` : '本页 0 条请求'}</span>
        <button type="button" disabled={busy || page.offset === 0} onClick={() => refresh(Math.max(0, page.offset - page.limit))}>上一页</button>
        <span>第 {Math.floor(page.offset / page.limit) + 1} 页</span>
        <button type="button" disabled={busy || !page.hasMore || page.offset + page.limit > ACCOUNTING_MAX_OFFSET} onClick={() => refresh(page.offset + page.limit)}>下一页</button>
      </ComponentRegistry>
    </>}
  </section>;
}
